/**
 * Component Name: ActionOnView
 *
 * Description:
 * The ActionOnView component automatically applies a specified class (`in-view`) to elements with
 * the class `action-on-view` when they enter the viewport. It enhances user engagement by enabling
 * on-scroll animations or transitions triggered when elements become visible.
 *
 * Usage:
 * - Add the `action-on-view` class to elements that should trigger animations or transitions when s
 *   crolled into view.
 * - Use CSS to define transitions for `.in-view` to style the elements once they are visible.
 * - No UI is rendered by this component; it works entirely in the background.
 *
 * Key Features:
 * - **Scroll and Resize Detection:** Dynamically tracks elements entering the viewport during
 *   scrolling or resizing.
 * - **DOM Mutation Handling:** Automatically applies the `in-view` class to new `action-on-view`
 *   elements added to the DOM after page load.
 * - **Performance Optimizations:** Efficiently manages event listeners and cleans up on unmount.
 *
 * Example:
 * ```tsx
 * <ActionOnView />
 *
 * <div className="action-on-view fade-in">This content fades in on view</div>
 * <div className="action-on-view slide-up">This content slides up on view</div>
 * ```
 *
 * Notes:
 * - Ensure `action-on-view` is added to the target elements in your HTML/JSX.
 * - Pair with CSS to define the desired transitions for `.in-view`.
 * - Compatible with dynamically loaded content due to the integrated `MutationObserver`.
 */

import { useEffect, useCallback } from 'react';
import './ActionOnView.styles.scss';

interface InViewObserverProps {
  threshold?: number; // Optional threshold value
}

const InViewObserver: React.FC<InViewObserverProps> = ({ threshold = 100 }) => {
  const handleInView = useCallback(() => {
    const elements = document.querySelectorAll('.action-on-view:not(.in-view)');
    elements.forEach((element) => {
      const rect = element.getBoundingClientRect();
      if (
        rect.top < window.innerHeight - threshold &&
        rect.bottom > threshold
      ) {
        element.classList.add('in-view');
      }
    });
  }, [threshold]);

  useEffect(() => {
    // Initial check for in-view elements
    handleInView();

    // Attach scroll and resize listeners
    window.addEventListener('scroll', handleInView);
    window.addEventListener('resize', handleInView);

    // Cleanup on component unmount
    return () => {
      window.removeEventListener('scroll', handleInView);
      window.removeEventListener('resize', handleInView);
    };
  }, [handleInView]);

  useEffect(() => {
    // Listen for DOM changes and re-check elements
    const observer = new MutationObserver(() => {
      handleInView();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Cleanup MutationObserver
    return () => observer.disconnect();
  }, [handleInView]);

  return null; // This component doesn't render anything
};

export default InViewObserver;
